import * as React from 'react'

import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'

const badgeVariants = cva(
  'inline-flex items-center justify-center gap-1 whitespace-nowrap rounded-2xl border border-solid font-semibold leading-normal shrink-0 overflow-hidden [&>svg]:pointer-events-none [&>svg]:shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-200',
  {
    variants: {
      variant: {
        warning: 'bg-warning-100 border-warning-icon text-warning-body',
        danger:
          'bg-badge-danger-bg border-badge-danger-stroke text-badge-danger-text',
        success:
          'bg-badge-success-bg border-badge-success-stroke text-badge-success-text',
        fill: 'bg-badge-fill-bg border-badge-fill-stroke text-badge-fill-text',
        open: 'bg-badge-open-bg border-badge-open-stroke text-badge-open-text',
        canceled:
          'bg-badge-canceled-bg border-badge-canceled-stroke text-badge-canceled-text',
        info: 'bg-badge-info-bg border-badge-info-stroke text-badge-info-text',
        outline:
          'bg-badge-outline-bg border-badge-outline-stroke text-badge-outline-text',
        'danger-light':
          'bg-badge-danger-light-bg border-badge-danger-light-stroke text-badge-danger-light-text',
        'danger-soft':
          'bg-badge-danger-soft-bg border-badge-danger-soft-stroke text-badge-danger-soft-text'
      },
      size: {
        md: 'text-caption-lg h-6 px-2 py-1 [&>svg]:size-[18px]',
        sm: 'text-caption-sm px-2 h-[19px] font-bold [&>svg]:size-[15px]'
      }
    },
    defaultVariants: {
      variant: 'fill',
      size: 'md'
    }
  }
)

type BadgeProps = React.ComponentProps<'span'> &
  VariantProps<typeof badgeVariants>

function Badge({
  className,
  variant,
  size,
  ...props
}: BadgeProps): React.JSX.Element {
  return (
    <span
      data-slot='badge'
      data-variant={variant}
      data-size={size}
      className={cn(badgeVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Badge, badgeVariants, type BadgeProps }
